import styled from '@emotion/styled';
import { useRef, useState, useMemo, useEffect } from 'react';
import { PencilSimple, CaretUp, CaretDown } from '@phosphor-icons/react';
import { AccessControl } from 'features/accessControl';
import { Tooltip } from 'components/tooltip';
import { t } from '@lingui/macro';
import type { CellContext } from '@tanstack/react-table';
import { useEscapeToClose, useRestoreFocusOnStay } from '../../../hooks';
import { useRowHeight } from '../../../providers/TableProvider';
import {
  useEditableContextOptional,
  useShouldSkipInlineCommit,
} from '../../OptimizedTableProvider';
import { DotsMenuButton } from '../../../components/DotsMenuButton';

export type EditableNumberCellProps<T extends { id: number | string }> = {
  context: CellContext<T, unknown>;
  /** Current cell value. Strings are accepted for compatibility with legacy callers. */
  value: string | number | null | undefined;
  /** Called when the user commits a valid value. `null` clears the field. */
  onUpdate: (newValue: number | null) => void | Promise<void>;
  /** External error (e.g. from a server response). Wins over local validation. */
  error?: string;
  /** When false, the cell is read-only. Empty values render blank instead of "_". */
  editable?: boolean;
  /** When true, the cell shows a muted/disabled appearance and is not editable. */
  disabled?: boolean;
  /** Hide the per-cell dots menu (default: false). */
  hideActions?: boolean;
  /** Optional caller-side validation. Returns an error message string or undefined. */
  validate?: (value: number | null) => string | undefined;
  /**
   * Lower bound for the value (inclusive). When `minExclusive` is true, the
   * bound is exclusive (used for `defaultQty`, where 0 is not allowed).
   */
  min?: number;
  minExclusive?: boolean;
  /** Upper bound (inclusive). */
  max?: number;
  /** Stepper increment (default: 1). */
  step?: number;
  /** Optional suffix shown after the number in read mode (e.g. "DKK"). */
  currency?: string;
  /** When true, decimal values are accepted. Defaults to false (integer-only). */
  allowDecimals?: boolean;
  /**
   * Maximum number of decimal places allowed (only relevant when allowDecimals is true).
   * When set, values with more decimal places than this limit are rejected.
   */
  allowedDecimals?: number;
  /**
   * Maximum number of digits allowed (excluding decimal point and minus sign).
   * When set, values whose digit count exceeds this limit are rejected.
   */
  maxChar?: number | null;
};

interface DocWithCaret {
  caretPositionFromPoint?: (clientX: number, clientY: number) => { offset: number } | null;
}

/**
 * Resolve the character offset closest to (x, y) using the standardized
 * caretPositionFromPoint API (Firefox, modern Chromium, Safari ≥ 18.4). Returns
 * null when the browser doesn't support it — the caret then falls back to the end
 * of the value. The non-standard caretRangeFromPoint is intentionally not used.
 */
function caretOffsetFromPoint(x: number, y: number): number | null {
  const doc: DocWithCaret = document;
  if (typeof doc.caretPositionFromPoint === 'function') {
    return doc.caretPositionFromPoint(x, y)?.offset ?? null;
  }
  return null;
}

const toDisplayString = (v: string | number | null | undefined): string =>
  v == null || v === '' ? '' : String(v);

export const EditableNumberCell = <T extends { id: number | string }>({
  context,
  value,
  onUpdate,
  error,
  editable = true,
  disabled = false,
  hideActions = false,
  validate,
  min,
  minExclusive = false,
  max,
  step = 1,
  currency,
  allowDecimals = false,
  allowedDecimals,
  maxChar,
}: EditableNumberCellProps<T>) => {
  const { row, column } = context;
  const rowHeight = useRowHeight();
  const editableCtx = useEditableContextOptional();
  const setEditableState = editableCtx?.setEditableState;
  const editLockedCell = editableCtx?.editLockedCell;
  const setEditLockedCell = editableCtx?.setEditLockedCell;
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(toDisplayString(value));
  const [localError, setLocalError] = useState<string | undefined>(undefined);
  const caretOffsetRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Mirror `isEditing` in a ref so the unmount-cleanup effect (which captures
  // values from mount time) can read the live value at the moment of unmount.
  const isEditingRef = useRef(false);

  // Refuse to enter edit while a sibling cell holds the edit lock (it has a
  // pending validation error). The user must resolve that cell first.
  const isLockedElsewhere =
    editLockedCell != null &&
    (editLockedCell.rowIndex !== row.index || editLockedCell.columnId !== column.id);

  const enterEdit = (newLocalValue: string) => {
    if (isLockedElsewhere) return;
    setLocalValue(newLocalValue);
    setLocalError(undefined);
    setIsEditing(true);
    isEditingRef.current = true;
    setEditableState?.({ rowIndex: row.index, columnId: column.id });
  };

  const exitEdit = () => {
    setIsEditing(false);
    isEditingRef.current = false;
    // Only release the shared slot if this cell still owns it, so a blur-save doesn't clear
    // the slot a just-clicked target cell already claimed in the same tick (DL-60264).
    setEditableState?.(prev =>
      prev?.rowIndex === row.index && prev?.columnId === column.id
        ? { rowIndex: null, columnId: null }
        : prev
    );
    setEditLockedCell?.(null);
  };

  // Acquire / release the table-wide edit lock based on whether this cell has
  // a pending validation error.
  useEffect(() => {
    if (!setEditLockedCell) return;
    if (isEditing && localError) {
      setEditLockedCell({ rowIndex: row.index, columnId: column.id });
    }
  }, [isEditing, localError, row.index, column.id, setEditLockedCell]);

  useEffect(
    () => () => {
      if (isEditingRef.current) {
        setEditableState?.({ rowIndex: null, columnId: null });
        setEditLockedCell?.(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot cleanup
    []
  );

  // Skip the blur-save while a navigation is being confirmed (flag raised synchronously by
  // the guard — on dialog open, or in a capture-phase pointerdown before this blur when an
  // in-app nav link is clicked) so the edit is never sent and "Leave" / Esc discard it (DL-60097).
  const shouldSkipCommit = useShouldSkipInlineCommit();

  const validateAndParse = (raw: string): { parsed: number | null; err?: string } => {
    const trimmed = raw.trim();
    if (trimmed === '') {
      const customErr = validate?.(null);
      return { parsed: null, err: customErr };
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      return { parsed: null, err: t`Enter a valid number` };
    }
    if (!allowDecimals && !Number.isInteger(parsed)) {
      return { parsed: null, err: t`Enter a whole number` };
    }
    if (allowDecimals && allowedDecimals != null && allowedDecimals > 0) {
      const dot = trimmed.indexOf('.');
      if (dot !== -1 && trimmed.length - dot - 1 > allowedDecimals) {
        return { parsed: null, err: t`At most ${allowedDecimals} decimal place(s) allowed` };
      }
    }
    if (maxChar != null && maxChar > 0) {
      const digits = trimmed.replace(/[^0-9]/g, '');
      if (digits.length > maxChar) {
        return { parsed: null, err: t`Maximum ${maxChar} digits allowed` };
      }
    }
    if (min != null) {
      const belowMin = minExclusive ? parsed <= min : parsed < min;
      if (belowMin) {
        if (minExclusive) {
          // Exclusive: `min` itself is invalid → "greater than N"
          if (min === 0) {
            return { parsed: null, err: t`The value must be greater than 0` };
          }
          return { parsed: null, err: t`The value must be greater than ${min}` };
        }
        // Inclusive: `min` itself is valid → "at least N"
        return { parsed: null, err: t`The value must be at least ${min}` };
      }
    }
    if (max != null && parsed > max) {
      return { parsed: null, err: t`The value must be at most ${max}` };
    }
    const customErr = validate?.(parsed);
    if (customErr) return { parsed: null, err: customErr };
    return { parsed };
  };

  const save = () => {
    // A leave-confirmation dialog is open (it stole focus and blurred us) — don't commit or
    // exit; the navigation guard owns this interaction and keeps the cell editing (DL-60097).
    if (shouldSkipCommit()) return;
    const { parsed, err } = validateAndParse(localValue);
    if (err) {
      setLocalError(err);
      return;
    }
    const original = toDisplayString(value);
    const next = parsed == null ? '' : String(parsed);
    if (next !== original) {
      onUpdate(parsed);
    }
    setLocalError(undefined);
    exitEdit();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      bumpBy(step);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      bumpBy(-step);
      return;
    }
    // Block obviously-bad keystrokes. Allow control chars, digits, minus, and
    // decimal point — non-integers are caught by the on-save validator so the
    // user sees a clear "Enter a whole number" message rather than silent input
    // suppression.
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !/[\d.-]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const bumpBy = (delta: number) => {
    const current = Number(localValue.trim());
    const isValidBase = Number.isFinite(current) && (allowDecimals || Number.isInteger(current));
    const base = isValidBase ? current : 0;
    // Round to 10 places to absorb IEEE-754 noise like 1.1 + 0.1 = 1.2000000000000002.
    // SQL column is decimal(25,5), so 10 places is safely inside the persisted scale.
    let nextNum = Number((base + delta).toFixed(10));
    if (min != null) {
      if (minExclusive) {
        if (nextNum <= min) nextNum = min + step;
      } else if (nextNum < min) {
        nextNum = min;
      }
    }
    if (max != null && nextNum > max) nextNum = max;
    setLocalValue(String(nextNum));
    setLocalError(undefined);
  };

  useEscapeToClose(isEditing, () => {
    setLocalError(undefined);
    exitEdit();
  });

  // When edit mode opens, focus the input and place the caret where the user
  // clicked (per spec, DL-59869), falling back to the end of the value when the
  // click offset is unavailable. Deferred to the next animation frame so the
  // browser's native focus handling for the originating click (whose target — the
  // read element — is unmounted as we enter edit mode) doesn't steal focus back,
  // which previously left the field needing a second click. Keyed on `isEditing`
  // so it runs once per edit, not on every keystroke re-render.
  useEffect(() => {
    if (!isEditing) return undefined;
    const input = inputRef.current;
    if (!input) return undefined;
    const raf = requestAnimationFrame(() => {
      input.focus();
      const len = input.value.length;
      const offset = caretOffsetRef.current === null ? len : Math.min(caretOffsetRef.current, len);
      input.setSelectionRange(offset, offset);
      caretOffsetRef.current = null;
      input.scrollLeft = offset === len ? input.scrollWidth : 0;
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when edit mode toggles
  }, [isEditing]);

  // After the leave-confirmation dialog closes via "Stay", MUI returns focus to whatever
  // opened the dialog (the nav link), not our input — pull focus back (DL-60097).
  useRestoreFocusOnStay(inputRef, isEditing);

  const displayString = toDisplayString(value);
  const displayValue = displayString || (editable ? '_' : '');
  const isInteractive = editable && !disabled;
  const showDotsMenu = isInteractive && !hideActions && row.original.id !== -1;

  // Derived errors computed from the current input on every render so they appear
  // immediately as the user types — mirrors overLimitError in EditableTextCell.
  const derivedError = (() => {
    if (!isEditing) return undefined;
    const trimmed = localValue.trim();
    if (trimmed === '') return undefined;
    if (allowDecimals && allowedDecimals != null && allowedDecimals > 0) {
      const dot = trimmed.indexOf('.');
      if (dot !== -1 && trimmed.length - dot - 1 > allowedDecimals) {
        return t`At most ${allowedDecimals} decimal place(s) allowed`;
      }
    }
    if (maxChar != null && maxChar > 0) {
      const digits = trimmed.replace(/[^0-9]/g, '');
      if (digits.length > maxChar) {
        return t`Maximum ${maxChar} digits allowed`;
      }
    }
    return undefined;
  })();

  const activeError = localError || derivedError || error;

  // Down-stepper disabled at the lower bound (or when empty + min would clamp at min)
  const currentNumeric = (() => {
    const n = Number(localValue.trim());
    return Number.isFinite(n) && (allowDecimals || Number.isInteger(n)) ? n : null;
  })();
  const downDisabled = (() => {
    if (min == null) return false;
    if (currentNumeric == null) return minExclusive ? min >= 0 : true;
    const floor = minExclusive ? min + step : min;
    return currentNumeric <= floor;
  })();
  const upDisabled = max != null && currentNumeric != null && currentNumeric >= max;

  const menuOptions = useMemo(
    () => [
      {
        label: t`Edit`,
        icon: <PencilSimple data-testid="pencil_simple_icon" size={16} />,
        onClick: () => enterEdit(displayString),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- enterEdit is stable across renders
    [displayString]
  );

  const handleCellClick = isInteractive
    ? (e: React.MouseEvent<HTMLDivElement>) => {
        caretOffsetRef.current = caretOffsetFromPoint(e.clientX, e.clientY);
        enterEdit(displayString);
      }
    : undefined;

  const readFallback = (
    <ReadWrapper
      $rowHeight={rowHeight}
      $editable={false}
      $disabled={disabled}
      title={activeError || undefined}
    >
      {displayValue}
    </ReadWrapper>
  );

  if (isEditing && !disabled) {
    return (
      <AccessControl write fallback={readFallback}>
        <Tooltip
          title={activeError ?? ''}
          open={!!activeError}
          placement="bottom-start"
          arrow
          error
          followCursor={false}
        >
          <InputWrapper $rowHeight={rowHeight}>
            <StyledInput
              ref={inputRef}
              type="text"
              inputMode={allowDecimals ? 'decimal' : 'numeric'}
              value={localValue}
              error={!!activeError}
              title={activeError || undefined}
              $rowHeight={rowHeight}
              onChange={e => {
                setLocalValue(e.target.value);
                if (localError) setLocalError(undefined);
              }}
              onBlur={save}
              onKeyDown={handleKeyDown}
            />
            <StepperColumn>
              <StepperButton
                type="button"
                aria-label={t`Increment`}
                disabled={upDisabled}
                onMouseDown={e => e.preventDefault()}
                onClick={() => bumpBy(step)}
              >
                <CaretUp size={10} />
              </StepperButton>
              <StepperButton
                type="button"
                aria-label={t`Decrement`}
                disabled={downDisabled}
                onMouseDown={e => e.preventDefault()}
                onClick={() => bumpBy(-step)}
              >
                <CaretDown size={10} />
              </StepperButton>
            </StepperColumn>
          </InputWrapper>
        </Tooltip>
      </AccessControl>
    );
  }

  const readContent = (
    <>
      <ReadWrapper
        $rowHeight={rowHeight}
        $editable={isInteractive}
        $disabled={disabled}
        title={activeError || undefined}
        onClick={handleCellClick}
      >
        {displayValue}
        {currency && displayString && <CurrencySuffix>{currency}</CurrencySuffix>}
      </ReadWrapper>
      {showDotsMenu && (
        <DotsMenuButtonWrapper>
          <DotsMenuButton menuOptions={menuOptions} />
        </DotsMenuButtonWrapper>
      )}
    </>
  );

  return (
    <AccessControl write fallback={readFallback}>
      {readContent}
    </AccessControl>
  );
};

const DotsMenuButtonWrapper = styled.div`
  max-width: 18px;
`;

const ReadWrapper = styled.div<{ $rowHeight: number; $editable: boolean; $disabled: boolean }>`
  display: block;
  width: 100%;
  line-height: ${p => p.$rowHeight}px;
  padding: 0 ${p => p.theme.s.sm};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: ${p => (p.$editable ? 'pointer' : 'default')};
  ${p => (p.$disabled ? `background-color: ${p.theme.palette.bg.disabledSubtle};` : '')};
  ${p => (p.$disabled ? `color: ${p.theme.palette.text.disabledSubtle};` : '')};
  &:hover {
    background-color: ${p => p.$editable && p.theme.palette.utility.grey[100]};
  }
`;

const CurrencySuffix = styled.span`
  margin-left: 8px;
  color: ${p => p.theme.palette.text.placeholder};
`;

const InputWrapper = styled.div<{ $rowHeight: number }>`
  display: flex;
  align-items: center;
  width: 100%;
  height: ${p => p.$rowHeight}px;
`;

const StyledInput = styled.input<{ error?: boolean; $rowHeight: number }>`
  flex: 1;
  min-width: 0;
  display: block;
  box-sizing: border-box;
  height: ${p => p.$rowHeight}px;
  line-height: ${p => p.$rowHeight}px;
  padding: 0 ${p => p.theme.s.sm};
  background: transparent;
  border: none;
  box-shadow: ${p =>
    p.error ? `inset 0 -1px 0 white, inset 0 -2px 0 ${p.theme.palette.border.errorSolid}` : 'none'};
  outline: none;
  font-family: inherit;
  font-size: inherit;
  color: ${p => (p.error ? p.theme.palette.fg.errorPrimary : 'inherit')};

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const StepperColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 18px;
  padding-right: 2px;
  gap: 0;
`;

const StepperButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 10px;
  width: 14px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: ${p => p.theme.palette.fg.secondary};

  &:first-of-type {
    margin-bottom: -1px;
  }
  &:last-of-type {
    margin-top: -1px;
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  &:hover:not(:disabled) {
    color: ${p => p.theme.palette.fg.primary};
  }
`;
